// src/pages/superadmin/PendingApprovals.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Cookies from 'js-cookie';
import {
  HiOutlineOfficeBuilding,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineCalendar,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineEye,
  HiOutlineSearch,
  HiOutlineRefresh,
  HiOutlineX,
  HiOutlineClock,
  HiOutlineUser,
  HiOutlineGlobeAlt,
  HiOutlineLocationMarker,
  HiOutlineDocumentText
} from 'react-icons/hi';
import { toast } from 'react-toastify';
import { superAdminService } from '../../../superAdminService';
import Header from '../../SuperAdmin/Header';
import Sidebar from '../../SuperAdmin/Sidebar';

const PendingApprovals = () => {
  const navigate = useNavigate();
  // ✅ Add sidebar state and toggle function
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [approvals, setApprovals] = useState([]);
  const [filteredApprovals, setFilteredApprovals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });

  const searchInputRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const superToken = Cookies.get('superAdminToken');
    if (!superToken) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [navigate]);

  // Filter approvals based on search
  useEffect(() => {
    if (debouncedSearchTerm.trim() === '') {
      setFilteredApprovals(approvals);
    } else {
      const filtered = approvals.filter(
        (college) =>
          college.collegeName?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          college.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          college.adminName?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
      setFilteredApprovals(filtered);
    }
  }, [debouncedSearchTerm, approvals]);

  const fetchData = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      const [pendingRes, statsRes] = await Promise.allSettled([
        superAdminService.getPendingApprovals(),
        superAdminService.getStats()
      ]);

      if (pendingRes.status === 'fulfilled' && pendingRes.value?.success) {
        const pendingData = pendingRes.value.data || [];
        const normalizedData = pendingData.map(item => ({
          ...item,
          id: item.id || item._id,
          _id: item._id || item.id,
          registeredAt: item.registeredAt || item.createdAt || new Date().toISOString()
        }));
        setApprovals(normalizedData);
        setFilteredApprovals(normalizedData);
      }

      if (statsRes.status === 'fulfilled' && statsRes.value?.success) {
        const statsData = statsRes.value.data?.stats || statsRes.value.data || {};
        setStats({
          pending: statsData.pending || 0,
          approved: statsData.approved || 0,
          rejected: statsData.rejected || 0
        });
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      toast.error('Failed to fetch pending approvals');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleViewDetails = (college) => {
    setSelectedCollege(college);
  };

  const handleApprove = async (college) => {
    const collegeId = college.id || college._id;
    
    if (!collegeId) {
      toast.error('College ID not found');
      return;
    }
    
    setProcessingId(collegeId);
    try {
      const response = await superAdminService.approveCollege(collegeId);
      if (response.success) {
        setApprovals(prev => prev.filter(c => (c.id || c._id) !== collegeId));
        setFilteredApprovals(prev => prev.filter(c => (c.id || c._id) !== collegeId));
        setSelectedCollege(null);
        toast.success(
          <div>
            <p className="font-medium">College Approved</p>
            <p className="text-sm opacity-90">{college.collegeName}</p>
          </div>,
          { icon: '✅' }
        );
        
        setStats(prev => ({
          ...prev,
          pending: Math.max(0, prev.pending - 1),
          approved: prev.approved + 1
        }));
      } else {
        toast.error(response.error || 'Failed to approve college');
      }
    } catch (error) {
      console.error('Error approving college:', error);
      toast.error('Failed to approve college');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedCollege || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    const collegeId = selectedCollege.id || selectedCollege._id;
    setProcessingId(collegeId);
    
    try {
      const response = await superAdminService.rejectCollege(collegeId, rejectReason);
      if (response.success) {
        setApprovals(prev => prev.filter(c => (c.id || c._id) !== collegeId));
        setFilteredApprovals(prev => prev.filter(c => (c.id || c._id) !== collegeId));
        setSelectedCollege(null);
        setShowRejectModal(false);
        setRejectReason('');
        toast.success(
          <div>
            <p className="font-medium">College Rejected</p>
            <p className="text-sm opacity-90">{selectedCollege.collegeName}</p>
          </div>,
          { icon: '❌' }
        );
        
        setStats(prev => ({
          ...prev,
          pending: Math.max(0, prev.pending - 1),
          rejected: prev.rejected + 1
        }));
      } else {
        toast.error(response.error || 'Failed to reject college');
      }
    } catch (error) {
      console.error('Error rejecting college:', error);
      toast.error('Failed to reject college');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const StatsCard = ({ title, value, icon: Icon, color = 'gray' }) => (
    <motion.div
      whileHover={{ y: -2 }}
      className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-gray-600" />
        </div>
      </div>
    </motion.div>
  );

  const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
          <div>
            <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-24"></div>
          </div>
        </div>
        <div className="w-16 h-5 bg-gray-200 rounded-full"></div>
      </div>
      <div className="space-y-2 mb-3">
        <div className="h-3 bg-gray-200 rounded w-full"></div>
        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
      </div>
      <div className="flex gap-2">
        <div className="flex-1 h-8 bg-gray-200 rounded"></div>
        <div className="flex-1 h-8 bg-gray-200 rounded"></div>
        <div className="w-10 h-8 bg-gray-200 rounded"></div>
      </div>
    </div>
  );

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200"
    >
      <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <HiOutlineOfficeBuilding className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">No Pending Approvals</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        {searchTerm 
          ? `No colleges match "${searchTerm}"`
          : 'All caught up! No colleges are waiting for approval.'}
      </p>
      {searchTerm && (
        <button
          onClick={() => {
            setSearchTerm('');
            searchInputRef.current?.focus();
          }}
          className="px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition inline-flex items-center gap-2"
        >
          <HiOutlineX className="w-5 h-5" />
          Clear Search
        </button>
      )}
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Pending Approvals" toggleSidebar={toggleSidebar} />
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-6 bg-gray-300 rounded w-16"></div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[...Array(4)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Pending Approvals" toggleSidebar={toggleSidebar} />
        
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-4 max-w-full"
          >
            {/* Header with Stats */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Pending Approvals</h1>
                <p className="text-sm text-gray-500 mt-1">
                  {filteredApprovals.length} college{filteredApprovals.length !== 1 ? 's' : ''} waiting for review
                </p>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition disabled:opacity-50 text-sm"
              >
                <HiOutlineRefresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </motion.button>
            </div>

            {/* Stats Cards */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 }
                }
              }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
            >
              <StatsCard title="Pending" value={stats.pending} icon={HiOutlineClock} />
              <StatsCard title="Approved" value={stats.approved} icon={HiOutlineCheckCircle} />
              <StatsCard title="Rejected" value={stats.rejected} icon={HiOutlineXCircle} />
            </motion.div>

            {/* Search Bar */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6 bg-white rounded-xl shadow-sm p-4 border border-gray-200"
            >
              <div className="relative">
                <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by college name, email or admin..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <HiOutlineX className="w-5 h-5" />
                  </button>
                )}
              </div>
            </motion.div>

            {/* Approvals List */}
            {filteredApprovals.length === 0 ? (
              <EmptyState />
            ) : (
              <motion.div
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: { staggerChildren: 0.1 }
                  }
                }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <AnimatePresence>
                  {filteredApprovals.map((college) => {
                    const collegeId = college.id || college._id;
                    return (
                      <motion.div
                        key={collegeId}
                        layout
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: { opacity: 1, y: 0 }
                        }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        whileHover={{ y: -2 }}
                        className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all border border-gray-200"
                      >
                        <div className="p-5">
                          {/* Header with Status */}
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center space-x-3 min-w-0">
                              <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                                <HiOutlineOfficeBuilding className="w-5 h-5 text-gray-600" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="font-semibold text-gray-800 text-base truncate max-w-[180px] sm:max-w-[220px]">
                                  {college.collegeName}
                                </h3>
                                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                  <HiOutlineCalendar className="w-3 h-3" />
                                  <span className="truncate">{formatDate(college.registeredAt || college.createdAt)}</span>
                                </div>
                              </div>
                            </div>
                            <span className="inline-flex items-center px-2.5 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-medium whitespace-nowrap ml-2">
                              <HiOutlineClock className="w-3 h-3 mr-1" />
                              Pending
                            </span>
                          </div>

                          {/* Details */}
                          <div className="space-y-2 mb-4">
                            <div className="flex items-center text-sm text-gray-600">
                              <HiOutlineMail className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{college.email}</span>
                            </div>
                            {college.contactNumber && (
                              <div className="flex items-center text-sm text-gray-600">
                                <HiOutlinePhone className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                                <span className="truncate">{college.contactNumber}</span>
                              </div>
                            )}
                            {college.adminName && (
                              <div className="flex items-center text-sm text-gray-600">
                                <HiOutlineUser className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                                <span className="truncate">Admin: {college.adminName}</span>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewDetails(college)}
                              className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm flex items-center justify-center gap-1"
                            >
                              <HiOutlineEye className="w-4 h-4" />
                              <span>View</span>
                            </button>
                            <button
                              onClick={() => handleApprove(college)}
                              disabled={processingId === collegeId}
                              className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50 text-sm flex items-center justify-center gap-1"
                            >
                              <HiOutlineCheckCircle className="w-4 h-4" />
                              <span>{processingId === collegeId ? '...' : 'Approve'}</span>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCollege(college);
                                setShowRejectModal(true);
                              }}
                              disabled={processingId === collegeId}
                              className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition disabled:opacity-50"
                              title="Reject"
                            >
                              <HiOutlineXCircle className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Details Modal */}
      <AnimatePresence>
        {selectedCollege && !showRejectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedCollege(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <HiOutlineOfficeBuilding className="w-5 h-5" />
                  College Details
                </h2>
                <button
                  onClick={() => setSelectedCollege(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <HiOutlineX className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-4 pb-4 border-b border-gray-200">
                  <div className="w-14 h-14 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                    <HiOutlineOfficeBuilding className="w-7 h-7 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-xl font-bold text-gray-800 break-words mb-1">
                      {selectedCollege.collegeName}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <HiOutlineMail className="w-4 h-4" />
                      <span className="break-words">{selectedCollege.email}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <HiOutlinePhone className="w-3 h-3" />
                      Contact Number
                    </p>
                    <p className="font-medium text-gray-800 break-words">
                      {selectedCollege.contactNumber || 'Not provided'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <HiOutlineGlobeAlt className="w-3 h-3" />
                      Website
                    </p>
                    <p className="font-medium text-gray-800 break-words">
                      {selectedCollege.website || 'Not provided'}
                    </p>
                  </div>
                  <div className="col-span-2 bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <HiOutlineLocationMarker className="w-3 h-3" />
                      Address
                    </p>
                    <p className="font-medium text-gray-800 break-words">
                      {selectedCollege.address || 'Not provided'}
                    </p>
                  </div>
                </div>

                {selectedCollege.description && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <HiOutlineDocumentText className="w-3 h-3" />
                      Description
                    </p>
                    <p className="text-sm text-gray-700 break-words">{selectedCollege.description}</p>
                  </div>
                )}

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="font-semibold text-gray-800 text-sm mb-3 flex items-center gap-2">
                    <HiOutlineUser className="w-4 h-4" />
                    Admin Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Name</p>
                      <p className="font-medium text-gray-800 break-words">
                        {selectedCollege.adminName || 'Not provided'}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Phone</p>
                      <p className="font-medium text-gray-800 break-words">
                        {selectedCollege.adminPhone || 'Not provided'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => handleApprove(selectedCollege)}
                    disabled={processingId === (selectedCollege.id || selectedCollege._id)}
                    className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                  >
                    <HiOutlineCheckCircle className="w-4 h-4" />
                    <span>{processingId === (selectedCollege.id || selectedCollege._id) ? 'Processing...' : 'Approve'}</span>
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm flex items-center justify-center gap-2"
                  >
                    <HiOutlineXCircle className="w-4 h-4" />
                    <span>Reject</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && selectedCollege && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowRejectModal(false);
              setRejectReason('');
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">Reject College</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Reject <span className="font-semibold">{selectedCollege.collegeName}</span>
                </p>
                
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-red-400"
                  rows="3"
                  autoFocus
                />

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectReason('');
                    }}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={!rejectReason.trim() || processingId === (selectedCollege.id || selectedCollege._id)}
                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {processingId === (selectedCollege.id || selectedCollege._id) ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Reject'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PendingApprovals;